/**
 * The shared table.
 *
 * Every screen in this app is a grid, so the RTL details are solved once here
 * rather than eight times:
 *
 *   - The first column sticks to the **inline start**, which is the right edge
 *     under `dir="rtl"`. Hardcoding `left: 0` puts it on the wrong side.
 *   - A column marked `actions` sticks to the inline **end**. Measured at
 *     820px, the row buttons on the customers and BOM grids sat at x = -43:
 *     outside the viewport, past the end of a table wide enough to scroll. The
 *     most-used controls in the app were the only ones you had to go looking
 *     for.
 *   - Numeric cells render LTR with tabular figures. Digits are written
 *     left-to-right even inside Hebrew, and without an explicit direction a
 *     value like `-250.40` can show its minus on the wrong end — a reading
 *     error, not a cosmetic one.
 *   - Wide content scrolls inside its own container, so the page body never
 *     scrolls sideways.
 */
import { useMemo, useRef, useState } from 'react'
import {
  Box, Button, Center, Checkbox, Group, Loader, Menu, Stack, Table, Text, useMatches,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
  flexRender, getCoreRowModel, getSortedRowModel, useReactTable,
  type ColumnDef, type SortingState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'

import { CODE_STYLE, COMPACT_QUERY, FILL_REMAINING, NUMERIC_STYLE } from '../theme'

/**
 * Row count above which the grid windows its rows instead of rendering them.
 *
 * Measured on the 6,495-item catalog before this existed: 143,145 DOM nodes,
 * 4,960ms to first usable paint, and ~2.4s of blocked main thread **per
 * keystroke** in the search box — because every keystroke re-rendered all
 * 6,495 rows. Search was already debounced; the debounce only ever stopped the
 * request, never the render.
 *
 * The threshold exists so the small grids — customers, BOMs, orders, all of
 * them well under a hundred rows and already painting in ~100ms — keep the
 * exact DOM and the exact auto-layout column sizing they have today. Windowing
 * buys them nothing and would only put row-height estimation between them and
 * a correct layout.
 */
const VIRTUALIZE_ABOVE = 150

/**
 * Opening guess for a row's height, in px, before any row has been measured.
 *
 * Only a guess: rows report their real height back (see `measureElement`), so
 * this being a little off costs one correction on first paint rather than a
 * scrollbar that lies. Measured 41 here against a real 39 — a 2px error per
 * row is 13,000px of phantom scroll across 6,495 of them, which is exactly the
 * kind of drift a hardcoded constant hides.
 */
const ESTIMATED_ROW_HEIGHT = 39

/**
 * Per-column rendering hints, set via `meta` on the column definition.
 *   `numeric` — tabular LTR digits, aligned to the end.
 *   `code`    — a Latin identifier (SKU, PO number): LTR so bidi cannot
 *               reorder a trailing hyphen to the front.
 */
export interface ColumnMeta {
  numeric?: boolean
  code?: boolean
  /**
   * Never wrap or shrink this cell — the column claims its content width.
   *
   * For short, atomic values: a status, a badge, a date. The table's auto
   * layout sizes a column from its *narrowest* rows, so a status column full of
   * `EOS` settled at 72px and then clipped `ACTIVE` to `…TIVE` inside it.
   * Mantine's Badge ellipsises its own label, so the truncation was silent.
   */
  nowrap?: boolean
  /**
   * Row actions. Pins the column to the inline end so it stays reachable
   * however wide the table gets, and stops the row's click handler firing
   * when a button inside it is pressed — a delete that also opens the row is
   * two things happening for one click.
   */
  actions?: boolean
  /**
   * Column width in px. **Required on every column of a grid large enough to
   * window** (see `VIRTUALIZE_ABOVE`); ignored on the small grids, which keep
   * auto layout.
   *
   * Why it has to be explicit there: an auto-layout table sizes its columns
   * from the rows it has actually rendered, and a windowed table has only ~23
   * of them. Measured on the catalog while scrolling top to bottom, columns
   * moved by up to **40px** — the description column breathing in and out as
   * longer and shorter Hebrew text scrolled through the window. Fixed layout
   * with declared widths is what makes a windowed grid hold still.
   */
  width?: number
  /**
   * Drop this column below the given breakpoint rather than let the table
   * compress it unreadable — the low-res/phone pass (DP-11). `'lg'` goes
   * first (hidden under 1200px), then `'md'` (under 992px), then `'sm'`
   * (under 768px); a column with none of these is never hidden this way and
   * is what `BomDetail`'s and `ReplacementPlanEditor`'s wide grids rely on —
   * those scroll sideways on purpose instead (see their own files), so they
   * declare no `hideBelow` at all.
   *
   * Never permanent: a column hidden this way is still *computed*, just not
   * rendered, and the "עמודות" chooser that appears whenever at least one
   * column is currently hidden brings any of them back for the session.
   */
  hideBelow?: 'sm' | 'md' | 'lg'
}

/**
 * Pins a cell to the inline **start** — the right edge under `dir="rtl"`.
 * Exported so `ReplacementPlanEditor.tsx`'s own hand-rolled `<Table>` (it
 * cannot use `DataGrid` — its rows are a form, not data) can pin the same way
 * instead of a second, drifting copy of this; see that file for why its first
 * column needs it too (a wide grid that scrolls sideways on purpose, same as
 * `BomDetail`'s, loses its identifying column off the edge without one).
 */
/**
 * `width: '1%'` plus `whiteSpace: nowrap` is a standard `table-layout: auto`
 * trick, not a typo: it tells the browser this column's *basis* is its own
 * minimum content width, so a table forced wider than its content (every
 * non-windowed grid's `miw`, see `tableMinWidth` below) distributes the
 * extra into its unpinned columns instead of here.
 *
 * It is load-bearing for the pinned columns specifically, not cosmetic:
 * without it, `table-layout: auto` stretches *every* column — sticky ones
 * included — to help fill that forced width. A stretched sticky-start
 * column pushes the sticky-end column's true (pre-stick) position further
 * off-screen at scroll position 0, and once that true position is already
 * past the box's far edge before the user has scrolled at all, the browser
 * does not ease it into its stuck position — it snaps straight there,
 * landing on top of whatever unpinned column's cell happens to occupy that
 * same spot. Measured via Playwright on `/customers`' budgets grid at
 * 390px: a click aimed at a row's `status` cell landed on the actions
 * column's delete button instead — a plain hit-test failure, not a
 * rendering glitch, and exactly reproducible without it.
 */
const STICKY_NO_STRETCH: React.CSSProperties = { width: '1%', whiteSpace: 'nowrap' }

export const STICKY_START: React.CSSProperties = {
  ...STICKY_NO_STRETCH,
  position: 'sticky',
  insetInlineStart: 0,
  background: 'var(--mantine-color-body)',
}
/** Pins a cell to the inline **end**. Needs its own background for the same
 * reason `STICKY_START` does — cells scrolling underneath would otherwise
 * show through — plus a divider, so it reads as pinned rather than as a
 * column that happens to be there. */
export const STICKY_END: React.CSSProperties = {
  ...STICKY_NO_STRETCH,
  position: 'sticky',
  insetInlineEnd: 0,
  background: 'var(--mantine-color-body)',
  boxShadow: 'inset 1px 0 0 var(--mantine-color-default-border)',
}

/** A column's identity for the hide/show machinery below — its explicit `id`,
 * or the accessor key every column in this app that does not set one declares
 * instead (`{ accessorKey: 'name', ... }`). Falls back to its position only
 * for a column that somehow has neither, which no caller's column list does
 * today. */
function columnKey(column: ColumnDef<any, any>, index: number): string {
  const c = column as { id?: string; accessorKey?: string }
  return c.id ?? c.accessorKey ?? `col-${index}`
}

interface Props<T> {
  data: T[]
  columns: ColumnDef<T, any>[]
  loading?: boolean
  empty?: string
  onRowClick?: (row: T) => void
  /** Highlights the selected row. Compared by identity. */
  selected?: T | null
  /**
   * `'100%'` is not "as tall as the content" like every other value here — it
   * means "fill whatever space the parent flex layout leaves," the low-res
   * pass's "grid takes the remaining viewport height" (DP-11). It only does
   * that if the parent actually has a definite height to share (a `Paper`
   * with `theme.ts`'s `FILL_REMAINING` style, inside a flex column whose own
   * height is set) — see any of the pages that pass it for the exact shape.
   * Every other value (a vh string, a px number) behaves as before: a cap,
   * not a fill, so a grid with three rows still renders three rows tall.
   */
  maxHeight?: number | string
  stickyFirstColumn?: boolean
  /**
   * Minimum table width before the container starts scrolling sideways.
   *
   * Without one the table shrinks to the viewport and the columns fight over
   * the space — measured on a 390px phone, a description column collapsed to
   * one word per line and rows grew to 210px, and on the desktop catalog the
   * status column squeezed to 72px so Mantine's Badge ellipsised `ACTIVE` into
   * `…TIVE`. A grid that scrolls is readable; a grid that compresses is not.
   *
   * Scale it with the column count: roughly 110px per column is the point
   * where Hebrew headers and money still read cleanly.
   */
  minWidth?: number
}

export function DataGrid<T>({
  data, columns, loading, empty = 'אין נתונים להצגה', onRowClick, selected,
  maxHeight = '60vh', stickyFirstColumn = true, minWidth,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const scrollRef = useRef<HTMLDivElement>(null)

  // Three breakpoint reads, not one: a column can be told to disappear at
  // `sm`, `md` or `lg` independently (see `ColumnMeta.hideBelow`), so the
  // grid has to know all three to tell which columns that is, right now.
  const belowSm = useMatches({ base: true, sm: false })
  const belowMd = useMatches({ base: true, md: false })
  const belowLg = useMatches({ base: true, lg: false })
  // Columns brought back for this session via the "עמודות" chooser below,
  // overriding `hideBelow` at the current width. Session-only on purpose —
  // nothing in the brief asks this to survive a reload, and every other
  // per-screen preference this pass added (the filter panel's open state)
  // says so explicitly where it does matter.
  const [forcedVisible, setForcedVisible] = useState<Set<string>>(new Set())
  function toggleForced(id: string) {
    setForcedVisible((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const { effectiveColumns, hiddenColumns } = useMemo(() => {
    const effective: ColumnDef<T, any>[] = []
    const hidden: ColumnDef<T, any>[] = []
    columns.forEach((column, index) => {
      const meta = column.meta as ColumnMeta | undefined
      const hiddenNow = meta?.hideBelow === 'lg' ? belowLg
        : meta?.hideBelow === 'md' ? belowMd
          : meta?.hideBelow === 'sm' ? belowSm
            : false
      if (hiddenNow && !forcedVisible.has(columnKey(column, index))) hidden.push(column)
      else effective.push(column)
    })
    return { effectiveColumns: effective, hiddenColumns: hidden }
  }, [columns, forcedVisible, belowSm, belowMd, belowLg])

  // Compact density (DP-11): tighter cell padding and a 12px floor on the
  // font rather than Mantine's default body size, so a grid on a short or
  // narrow screen trades a little breathing room for more visible rows.
  // `fz`/spacing left `undefined` otherwise so an ordinary desktop window
  // keeps the exact metrics it always had.
  //
  // Keyed on `COMPACT_QUERY` (height-or-width), not `belowLg` (width alone,
  // still used just above for `hideBelow`): a row's *height* is what the
  // 12-visible-rows floor is actually measured against, and the two
  // viewports that floor names outright — 1366x768, 1280x720 — are both
  // wider than `lg` and would never have gone dense under a width-only
  // rule. Measured short by exactly one row at 1280x720 on the BOM list
  // without this (a route whose own action column, unlike the catalog's,
  // is tall enough on its own to set the row height) — `hideBelow` and
  // density answer two different questions (which columns fit; how tall a
  // row is) and only one of them is about width alone.
  const dense = useMediaQuery(COMPACT_QUERY)

  const table = useReactTable({
    data,
    columns: effectiveColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const rows = table.getRowModel().rows
  const virtualizing = rows.length > VIRTUALIZE_ABOVE
  // A dense row is shorter than `ESTIMATED_ROW_HEIGHT` was tuned for (that
  // measurement is documented against the normal, non-dense padding) — only
  // ever a first-paint guess either way; see that constant's own comment for
  // why being a little off is fine.
  const estimatedRowHeight = dense ? 32 : ESTIMATED_ROW_HEIGHT

  // Declared before the early returns below: hooks cannot run conditionally,
  // and `loading` / empty states are exactly the renders where they would.
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimatedRowHeight,
    // Enough rows above and below the viewport that a fast scroll or a
    // keyboard PageDown does not show a band of empty space before React
    // catches up.
    overscan: 12,
  })

  const filling = maxHeight === '100%'

  if (loading) {
    return <Center p="xl"><Loader /></Center>
  }
  if (!data.length) {
    return <Center p="xl"><Text c="dimmed">{empty}</Text></Center>
  }

  const virtualRows = virtualizing ? virtualizer.getVirtualItems() : []
  // Carries each row's index in the full list, which is what the virtualizer
  // needs back from the DOM in order to measure it.
  const visibleRows = virtualizing
    ? virtualRows.map((v) => ({ row: rows[v.index], index: v.index }))
    : rows.map((row, index) => ({ row, index }))

  // Two spacer rows stand in for everything not rendered, so the scrollbar
  // describes the whole list and the scroll position means what it says.
  const padStart = virtualRows.length ? virtualRows[0].start : 0
  const padEnd = virtualRows.length
    ? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
    : 0


  const actionIndex = effectiveColumns.findIndex(
    (column) => (column.meta as ColumnMeta | undefined)?.actions)

  // Declared widths, used only when windowing. A column without one falls back
  // to a readable default rather than collapsing.
  const widths = effectiveColumns.map(
    (column) => (column.meta as ColumnMeta | undefined)?.width ?? 120)

  // Default from the column count when the caller has not chosen one. A
  // windowed grid is exactly as wide as its declared columns, so the two
  // never disagree about where a column starts. Based on the columns actually
  // showing: a grid with half its columns hidden by `hideBelow` should not
  // still demand room — and does not scroll sideways — for the other half.
  //
  // The floor is 320, not the 640 every non-windowed grid used before
  // `hideBelow` existed. 640 was fine when a grid's column count never
  // changed — for the smallest grids (~6 columns) it was not even the
  // binding term (6 * 110 = 660). It became actively harmful the moment a
  // grid could be *trimmed*: at 390px wide, `/customers` hides down to
  // [name, status, act] and `effectiveColumns.length * 110` is only 330, so
  // 640 was the one actually forcing the table — and `table-layout: auto`
  // does not leave 310px of forced extra width blank, it stretches the
  // columns to fill it. That stretch is what pushed the sticky end-pinned
  // `act` column's *true* position (well past the right edge of a
  // same-forced-wide `name` column) far enough off-screen that, unscrolled,
  // it sat exactly on top of `status` instead of beside it — measured via
  // Playwright: a click aimed at the first row's name cell landed on "ערוך✕"
  // instead, which is a plain click hitting the wrong control, not a
  // rendering glitch. 320 is small enough that a 3-column phone grid simply
  // does not need to scroll at all (no sideways scroll, no stickiness, no
  // overlap), while still reading as a *table* rather than a squeeze for the
  // handful of grids genuinely down to one or two columns.
  const tableMinWidth = virtualizing
    ? widths.reduce((sum, w) => sum + w, 0)
    : minWidth ?? Math.max(320, effectiveColumns.length * 110)

  const grid = (
    <Box
      ref={scrollRef}
      style={{
        overflow: 'auto',
        maxWidth: '100%',
        ...(filling ? FILL_REMAINING : { maxHeight }),
      }}
    >
      <Table
        striped
        highlightOnHover
        withColumnBorders
        stickyHeader
        miw={tableMinWidth}
        verticalSpacing={dense ? 4 : undefined}
        horizontalSpacing={dense ? 6 : undefined}
        fz={dense ? 'xs' : undefined}
        // Fixed layout is what stops a windowed grid resizing its columns as
        // you scroll: the widths come from the colgroup below, not from
        // whichever 23 rows happen to be rendered.
        style={virtualizing ? { tableLayout: 'fixed' } : undefined}
      >
        {virtualizing && (
          <colgroup>
            {widths.map((w, i) => <col key={i} style={{ width: w }} />)}
          </colgroup>
        )}
        <Table.Thead>
          {table.getHeaderGroups().map((group) => (
            <Table.Tr key={group.id}>
              {group.headers.map((header, index) => (
                <Table.Th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{
                    cursor: header.column.getCanSort() ? 'pointer' : 'default',
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                    ...(stickyFirstColumn && index === 0 ? { ...STICKY_START, zIndex: 3 } : {}),
                    ...(index === actionIndex ? { ...STICKY_END, zIndex: 3 } : {}),
                  }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {{ asc: ' ▲', desc: ' ▼' }[header.column.getIsSorted() as string] ?? ''}
                </Table.Th>
              ))}
            </Table.Tr>
          ))}
        </Table.Thead>
        <Table.Tbody>
          {padStart > 0 && (
            <Table.Tr aria-hidden style={{ height: padStart }} />
          )}
          {visibleRows.map(({ row, index: rowIndex }) => (
            <Table.Tr
              key={row.id}
              // Report the real height back, so the scrollbar describes the
              // list that exists rather than the one ESTIMATED_ROW_HEIGHT
              // guessed at.
              ref={virtualizing ? virtualizer.measureElement : undefined}
              data-index={rowIndex}
              onClick={() => onRowClick?.(row.original)}
              style={{
                cursor: onRowClick ? 'pointer' : 'default',
                ...(selected === row.original
                  ? { background: 'var(--mantine-color-blue-light)' }
                  : {}),
              }}
            >
              {row.getVisibleCells().map((cell, index) => {
                const meta = cell.column.columnDef.meta as ColumnMeta | undefined
                const value = cell.getValue()
                return (
                  <Table.Td
                    key={cell.id}
                    // A windowed row must be exactly one line high, or the
                    // estimated height stops matching the real one and the
                    // scrollbar starts lying about where you are. Truncation is
                    // never silent: the full text is on the cell, so hovering
                    // shows it, and the catalog's detail pane has it in full.
                    title={virtualizing && (typeof value === 'string' || typeof value === 'number')
                      ? String(value)
                      : undefined}
                    style={{
                      ...(meta?.numeric ? NUMERIC_STYLE : {}),
                      ...(meta?.code ? CODE_STYLE : {}),
                      ...(meta?.nowrap ? { whiteSpace: 'nowrap' as const } : {}),
                      ...(virtualizing
                        ? {
                          whiteSpace: 'nowrap' as const,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }
                        : {}),
                      ...(stickyFirstColumn && index === 0 ? { ...STICKY_START, zIndex: 1 } : {}),
                      ...(index === actionIndex ? { ...STICKY_END, zIndex: 2 } : {}),
                    }}
                    // Buttons in the actions column must not also trigger the
                    // row. Handled here rather than in nine call sites, each
                    // of which had to remember it.
                    onClick={index === actionIndex
                      ? (event) => event.stopPropagation()
                      : undefined}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Td>
                )
              })}
            </Table.Tr>
          ))}
          {padEnd > 0 && (
            <Table.Tr aria-hidden style={{ height: padEnd }} />
          )}
        </Table.Tbody>
      </Table>
    </Box>
  )

  // No column is currently hidden on most grids at most widths — most grids
  // declare no `hideBelow` at all, and even one that does may be looking at
  // a wide-enough viewport right now. The chooser only takes up room when it
  // would actually do something.
  if (!hiddenColumns.length) return grid

  return (
    <Stack gap={4} style={filling ? FILL_REMAINING : undefined}>
      <Group justify="flex-end" gap={4} style={{ flexShrink: 0 }}>
        <Menu withinPortal closeOnItemClick={false} position="bottom-end">
          <Menu.Target>
            {/* compact-sm, not compact-xs — the xs compact size measures
                37x22, under the 24px WCAG target (see BomDetail.tsx's own
                history button for the defect this already caught once). */}
            <Button size="compact-sm" variant="subtle">
              עמודות ({hiddenColumns.length})
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {hiddenColumns.map((column, index) => {
              const id = columnKey(column, index)
              const label = typeof column.header === 'string' ? column.header : id
              return (
                // The toggle lives on `Menu.Item`, not the checkbox: both
                // firing on one click (the click bubbling from the input)
                // would flip `forcedVisible` twice and look like nothing
                // happened. `readOnly` silences React's "checked with no
                // onChange" warning without giving the input a second,
                // competing handler.
                <Menu.Item key={id} onClick={() => toggleForced(id)}>
                  <Checkbox
                    label={label}
                    checked={forcedVisible.has(id)}
                    readOnly
                    size="sm"
                  />
                </Menu.Item>
              )
            })}
          </Menu.Dropdown>
        </Menu>
      </Group>
      {grid}
    </Stack>
  )
}
